import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../ui/api';

/**
 * The search index, kept in memory for the whole session.
 *
 * Not useCachedData: that hook fetches on mount and on its deps, but the
 * search bar is mounted once for the whole session, so a student added
 * an hour ago would never be findable. Instead:
 *
 *   - prefetched shortly after sign-in, so the first keystroke is instant
 *   - every time the search opens, one tiny /data-version check; if
 *     anything changed since the index was built, it is refetched in the
 *     background while the old one keeps answering (never a blank list)
 *   - memory only: it holds names and phone numbers, which the cache
 *     policy (ui/cache/index.js) never writes to disk.
 */
export default function useSearchIndex() {
  const [state, setState] = useState({ data: null, loading: false, refreshing: false, error: null });
  const dataRef = useRef(null);
  const inflight = useRef(null);

  const fetchIndex = useCallback(() => {
    if (inflight.current) return inflight.current;
    setState((s) => ({ ...s, loading: !dataRef.current, refreshing: !!dataRef.current, error: null }));
    inflight.current = api.searchIndex()
      .then((data) => {
        dataRef.current = data;
        setState({ data, loading: false, refreshing: false, error: null });
      })
      .catch((err) => {
        // A failed refresh keeps the index we have; only a first load
        // with nothing to show is an error.
        setState((s) => ({ ...s, loading: false, refreshing: false, error: dataRef.current ? null : err.message }));
      })
      .finally(() => { inflight.current = null; });
    return inflight.current;
  }, []);

  /** Called when the search opens: refetch only if the data moved. */
  const ensureFresh = useCallback(async () => {
    if (!dataRef.current) return fetchIndex();
    try {
      const v = await api.dataVersion();
      if (String(v) !== String(dataRef.current.version)) return fetchIndex();
    } catch { /* offline: the index we have is the best answer */ }
    return null;
  }, [fetchIndex]);

  useEffect(() => {
    const id = setTimeout(fetchIndex, 800);
    return () => clearTimeout(id);
  }, [fetchIndex]);

  return { ...state, ensureFresh, retry: fetchIndex };
}
